import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import { Grid, GridColumn, GridSortChangeEvent, GridFilterChangeEvent, GridHeaderSelectionChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { orderBy, filterBy, SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import './tmi.css';

import moment from 'moment';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const DateCell = (props: GridCellProps) => {
    const val = props.dataItem[props.field!];
    return <td>{val ? moment(val).format('DD/MM/YYYY HH:mm') : ''}</td>;
};

const TruncCell = (props: GridCellProps) => {
    const val = props.dataItem[props.field!] ?? '';
    const text = String(val);
    return (
        <td title={text} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
        </td>
    );
};

const TruncCellLeft = (props: GridCellProps) => {
    const val = props.dataItem[props.field!] ?? '';
    const text = String(val);
    return (
        <td title={text} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
            {text}
        </td>
    );
};

const BooleanCell = (props: GridCellProps) => {
    const value: boolean = props.dataItem[props.field!] ?? false;
    return (
        <td style={{ textAlign: 'center' }}>
            {value ? '✓' : ''}
        </td>
    );
};

type ImportTransactionsProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: ImportTransactionsState;
    onSaveState?: (menuItemId: number, state: ImportTransactionsState) => void;
}

interface ImportTransactionRow {
    transactionID: string;
    projectID: number;
    projectName: string;
    databaseName: string;
    templateName: string;
    transactionDate: string | null;
    startedAt: string | null;
    runningTime: string;
    transactionStatus: string;
    isTestMode: boolean;
    userName: string;
    fileNames: string;
}

interface ImportTransactionsState {
    loading: boolean;
    data: ImportTransactionRow[];
    displayData: ImportTransactionRow[];
    errorMsg: string;
    autoRefresh: boolean;
    lastRefreshed: Date | null;
    sort: SortDescriptor[];
    filter: CompositeFilterDescriptor | undefined;
    filterTexts: { [key: string]: string };
    headerFocusField?: string | null;
    selectionVersion: number;
    skip: number;
    pageSize: number;

    
    showErrorsDlg: boolean;
    errorsLoading: boolean;
    errorsText: string;
    errorsList: any[];
    errorsDlgTitle: string;

}

const AUTO_REFRESH_INTERVAL = 60000;

class ImportTransactions extends React.Component<ImportTransactionsProps, ImportTransactionsState> {
    private refreshTimer: ReturnType<typeof setInterval> | null = null;
    private selectedIds = new Set<string>();
    private rowCheckboxRefs = new Map<string, HTMLInputElement>();
    private selectionVersion = 0;
    private setReadyToRunBtnRef: HTMLButtonElement | null = null;
    private resetSelectedBtnRef: HTMLButtonElement | null = null;
    private headerCellRenderers: { [key: string]: any } = {};
    private headerSelectRefs: Map<string, HTMLSelectElement> = new Map();

    constructor(props: ImportTransactionsProps) {
        super(props);

        // Restore the full previous page state (including filters) when navigating
        // back to this page after viewing another menu item; otherwise start fresh.
        if (props.stateToLoad) {
            this.state = { ...props.stateToLoad };
        } else {
            this.state = {
                loading: false,
                data: [],
                displayData: [],
                errorMsg: '',
                autoRefresh: false,
                lastRefreshed: null,
                sort: [{ field: 'transactionDate', dir: 'desc' }],
                filter: undefined,
                filterTexts: {},
                headerFocusField: null,
                selectionVersion: 0,
                skip: 0,
                pageSize: 30,

                showErrorsDlg: false,
                errorsLoading: false,
                errorsText: '',
                errorsList: [],
                errorsDlgTitle: '',

            }
        }

        // Create stable headerCell renderer functions once so they are not recreated each render
        const fields = [
            { f: 'transactionDate', t: 'Transaction Date' },
            { f: 'startedAt', t: 'Started At' },
            { f: 'runningTime', t: 'Hours' },
            { f: 'projectName', t: 'Project' },
            { f: 'databaseName', t: 'Database' },
            { f: 'templateName', t: 'Template' },
            { f: 'fileNames', t: 'Files' },
            { f: 'userName', t: 'User' },
            { f: 'transactionStatus', t: 'Status' },
            { f: 'isTestMode', t: 'Test Mode' },
        ];

        fields.forEach(col => {
            const f = col.f;
            const title = col.t;
            if (f === 'isTestMode') {
                this.headerCellRenderers[f] = (props: any) => {
                    const val = this.state.filterTexts?.[f] ?? '';
                    const focused = this.state.headerFocusField === f;
                    return (
                        <th className="tmi-header-filter">
                            <div className="k-link" style={{ display: 'none' }}>
                                { /* isTestMode column is not sortable; keep k-link hidden */ }
                            </div>
                            <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                                <label>{title}</label>
                                <select
                                    className="k-input k-input-sm"
                                    value={val}
                                    onChange={(e) => this.handleHeaderFilterChange(f, e as unknown as React.ChangeEvent<HTMLInputElement>)}
                                    onFocus={() => this.handleHeaderFocus(f)}
                                    onBlur={() => this.handleHeaderBlur()}
                                    onMouseDown={(e) => { e.stopPropagation(); }}
                                >
                                    <option value="">All</option>
                                    <option value="true">True</option>
                                    <option value="false">False</option>
                                </select>
                            </div>
                        </th>
                    );
                };
            } else {
                this.headerCellRenderers[f] = (props: any) => {
                    const val = this.state.filterTexts?.[f] ?? '';
                    const focused = this.state.headerFocusField === f;
                    const sortDir = (this.state.sort && this.state.sort.length > 0 && this.state.sort[0].field === f) ? (this.state.sort[0].dir as string) : undefined;
                    const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.toggleSort(f);
                    };
                    const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                    };
                    return (
                        <th className="tmi-header-filter">
                            <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                            <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                                <label onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                                    <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                                </label>
                                <input
                                    type="text"
                                    className="k-input k-input-sm"
                                    value={val}
                                    onChange={(e) => this.handleHeaderFilterChange(f, e)}
                                    onFocus={() => this.handleHeaderFocus(f)}
                                    onBlur={() => this.handleHeaderBlur()}
                                    onMouseDown={(e) => { e.stopPropagation(); }}
                                />
                            </div>
                        </th>
                    );
                };
            }
        });
    }

    handleHeaderFocus = (field: string) => {
        this.setState({ headerFocusField: field });
    }

    handleHeaderBlur = () => {
        this.setState({ headerFocusField: null });
    }

    toggleSort = (field: string) => {
        const current = this.state.sort && this.state.sort.length > 0 ? this.state.sort[0] : undefined;
        let newSort: SortDescriptor[] = [];
        if (!current || current.field !== field) {
            newSort = [{ field, dir: 'asc' }];
        } else if (current.dir === 'asc') {
            newSort = [{ field, dir: 'desc' }];
        } else {
            newSort = [];
        }
        this.setState({ sort: newSort });
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps: ImportTransactionsProps, prevState: ImportTransactionsState) {
        if (prevState.data !== this.state.data ||
            prevState.sort !== this.state.sort ||
            prevState.filter !== this.state.filter) {
            const { data, sort, filter } = this.state;
            this.setState({ displayData: orderBy(filter ? filterBy(data, filter) : data, sort), skip: 0 });
        }
    }

    componentWillUnmount() {
        this.clearRefreshTimer();
        if (this.props.onSaveState && this.props.menuItemId !== undefined) {
            this.props.onSaveState(this.props.menuItemId, this.state);
        }
    }

    clearRefreshTimer = () => {
        if (this.refreshTimer !== null) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    toggleAutoRefresh = () => {
        const enabling = !this.state.autoRefresh;
        this.setState({ autoRefresh: enabling });
        if (enabling) {
            this.refreshTimer = setInterval(this.fetchData, AUTO_REFRESH_INTERVAL);
        } else {
            this.clearRefreshTimer();
        }
    }

    handleSortChange = (e: GridSortChangeEvent) => {
        this.setState({ sort: e.sort });
    }

    handleFilterChange = (e: GridFilterChangeEvent) => {
        this.setState({ filter: e.filter ?? undefined });
    }

    handleHeaderFilterChange = (field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const val = e.target.value;
        this.setState(prev => ({ filterTexts: { ...prev.filterTexts, [field]: val }, skip: 0 }));
    }

    handlePageChange = (e: GridPageChangeEvent) => {
        this.setState({ skip: e.page.skip, pageSize: e.page.take });
    }

    setRowSelected = (id: string, selected: boolean) => {
        selected ? this.selectedIds.add(id) : this.selectedIds.delete(id);
        const el = this.rowCheckboxRefs.get(id);
        if (el) el.checked = selected;
        if (this.setReadyToRunBtnRef) this.setReadyToRunBtnRef.disabled = this.selectedIds.size === 0;
        if (this.resetSelectedBtnRef) this.resetSelectedBtnRef.disabled = this.selectedIds.size === 0;
    }

    handleHeaderSelectionChange = (e: GridHeaderSelectionChangeEvent) => {
        const checked = (e.syntheticEvent.target as HTMLInputElement).checked;
        if (checked) {
            this.state.displayData.forEach(r => this.selectedIds.add(r.transactionID));
        } else {
            this.selectedIds.clear();
        }
        this.rowCheckboxRefs.forEach(el => { el.checked = checked; });
        if (this.setReadyToRunBtnRef) this.setReadyToRunBtnRef.disabled = this.selectedIds.size === 0;
        if (this.resetSelectedBtnRef) this.resetSelectedBtnRef.disabled = this.selectedIds.size === 0;
        this.selectionVersion++;
        this.setState({ selectionVersion: this.selectionVersion });
    }

    renderCheckboxCell = (props: GridCellProps) => {
        const id = props.dataItem.transactionID;
        return (
            <td>
                <input
                    type="checkbox"
                    key={`${id}-${this.selectionVersion}`}
                    defaultChecked={this.selectedIds.has(id)}
                    ref={(el) => {
                        if (el) this.rowCheckboxRefs.set(id, el);
                        else this.rowCheckboxRefs.delete(id);
                    }}
                    onChange={(e) => this.setRowSelected(id, e.target.checked)}
                />
            </td>
        );
    }

    resetSelected = () => {
        this.callStatusApi('/api/TMI/SetTransactionStatusUploading');
    }

    setReadyToRun = () => {
        this.callStatusApi('/api/TMI/SetTransactionStatusNew');
    }

    callStatusApi = (endpoint: string) => {
        const transactions = this.state.data
            .filter(r => this.selectedIds.has(r.transactionID))
            .map(r => ({ transactionID: r.transactionID, databaseName: r.databaseName }));

        if (transactions.length === 0) return;

        const url = this.props.userInf.currProject.apiUrl + endpoint;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.userInf.token
            },
            body: JSON.stringify(transactions)
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        this.fetchData();
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...this.props.userInf, isAuthorised: false };
                        this.props.updateUserInfo(uInf);
                        break;
                    default:
                        this.setState({ errorMsg: 'Error: ' + res.callStatusMessage });
                }
            })
            .catch(err => {
                this.setState({ errorMsg: 'Error: ' + err.toString() });
            });
    }

    fetchData = () => {
        this.setState({ loading: true, errorMsg: '' });

        const url = this.props.userInf.currProject.apiUrl + '/api/TMI/GetImportTransactionList';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.userInf.token
            }
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        const newData: ImportTransactionRow[] = res.results;
                        const { sort, filter } = this.state;
                        this.setState({
                            data: newData,
                            displayData: orderBy(filter ? filterBy(newData, filter) : newData, sort),
                            loading: false,
                            lastRefreshed: new Date(),
                        });
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...this.props.userInf, isAuthorised: false };
                        this.props.updateUserInfo(uInf);
                        break;
                    default:
                        this.setState({ errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false });
                }
            })
            .catch(err => {
                this.setState({ errorMsg: 'Error fetching data: ' + err.toString(), loading: false });
            });
    }


    openErrorsDialog = (transactionID: string, row?: ImportTransactionRow) => {
        this.setState({ showErrorsDlg: true, errorsLoading: true, errorsText: '', errorsDlgTitle: `Transaction Errors: ${transactionID}` });

        const url = this.props.userInf.currProject.apiUrl + '/api/TMI/GetImportTransactionErrors';

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.userInf.token
            },
            body: JSON.stringify({ TransactionID: transactionID, DatabaseName: row ? row.databaseName : '' })
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        if (Array.isArray(res.results) && res.results.length > 0 && typeof res.results[0] === 'object') {
                            this.setState({ errorsList: res.results, errorsText: '', errorsLoading: false });
                        } else {
                            const txt = Array.isArray(res.results) ? res.results.join('\n') : (res.results || '').toString();
                            this.setState({ errorsText: txt, errorsList: [], errorsLoading: false });
                        }
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...this.props.userInf, isAuthorised: false };
                        this.props.updateUserInfo(uInf);
                        break;
                    default:
                        this.setState({ errorsText: 'Error fetching errors: ' + res.callStatusMessage, errorsLoading: false });
                }
            })
            .catch(err => {
                this.setState({ errorsText: 'Error fetching errors: ' + err.toString(), errorsLoading: false });
            });
    }

    closeErrorsDialog = () => {
        this.setState({ showErrorsDlg: false, errorsText: '', errorsLoading: false, errorsList: [], errorsDlgTitle: '' });
    }

    renderErrorsCell = (props: GridCellProps) => {
        const status = (props.dataItem.transactionStatus || '').toString();
        const id = props.dataItem.transactionID;
        const shouldShow = status === 'Failed' || status === 'Successful (Warnings)';
        return (
            <td>
                {shouldShow ? (
                    <button className="k-button" onClick={() => this.openErrorsDialog(id, props.dataItem)}>View Errors</button>
                ) : null}
            </td>
        );
    }


    render() {
        const { loading, errorMsg, autoRefresh, lastRefreshed, displayData, filter, skip, pageSize } = this.state;
        const filters = this.state.filterTexts || {};
        const filteredDisplay = Object.keys(filters).length === 0 || Object.values(filters).every(v => !v)
            ? displayData
            : displayData.filter(item => {
                return Object.entries(filters).every(([f, q]) => {
                    if (!q) return true;
                    const val = (item as any)[f];
                    const ql = q.trim().toLowerCase();
                    if (ql === 'true' || ql === 'false') {
                        return String(val ?? '').toLowerCase() === ql;
                    }
                    return String(val ?? '').toLowerCase().includes(ql);
                });
            });

        const allSelected = filteredDisplay.length > 0 && filteredDisplay.every(r => this.selectedIds.has(r.transactionID));
        const pageData = filteredDisplay.slice(skip, skip + pageSize);

        return (
            <div className="import-transactions-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="import-transactions-toolbar" style={{ display: 'flex', flexDirection: 'column', width: '100%', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <button onClick={this.fetchData} disabled={loading} title="Refresh">
                            <span className="k-icon k-font-icon k-i-reload" />
                        </button>
                        <button onClick={this.toggleAutoRefresh} title={autoRefresh ? 'Auto Refresh On (click to disable)' : 'Auto Refresh Off (click to enable)'}
                            style={{ marginLeft: '6px', fontWeight: autoRefresh ? 'bold' : 'normal', color: autoRefresh ? 'green' : undefined }}>
                            <span className="k-icon k-font-icon k-i-clock" /> Auto Refresh {autoRefresh ? 'ON' : 'OFF'}
                        </button>
                        {lastRefreshed && (
                            <span style={{ marginLeft: '12px', fontSize: '0.85em', color: '#555' }}>
                                Last refreshed: {lastRefreshed.toLocaleTimeString()}
                            </span>
                        )}
                        <button
                            ref={(el) => {
                                this.resetSelectedBtnRef = el;
                                if (el) el.disabled = this.selectedIds.size === 0;
                            }}
                            onClick={this.resetSelected}
                            style={{ marginLeft: '12px' }}
                        >
                            Reset Selected
                        </button>
                        <button
                            ref={(el) => {
                                this.setReadyToRunBtnRef = el;
                                if (el) el.disabled = this.selectedIds.size === 0;
                            }}
                            onClick={this.setReadyToRun}
                            style={{ marginLeft: '6px' }}
                        >
                            Set Ready To Run
                        </button>
                        {loading && <span style={{ marginLeft: '12px' }}><span className="k-icon k-font-icon k-i-loading" /></span>}
                    </div>
                </div>
                {errorMsg && <div className="import-transactions-error">{errorMsg}</div>}
                <Grid
                    data={pageData}
                    total={filteredDisplay.length}
                    style={{ flex: 1, minHeight: 0 }}
                    resizable
                    /* disable Kendo's built-in filter UI; using per-column header inputs instead */
                    filterable={false}
                    filter={filter}
                    onFilterChange={this.handleFilterChange}
                    pageable={{ pageSizes: [30, 50, 100] }}
                    skip={skip}
                    take={pageSize}
                    onPageChange={this.handlePageChange}
                    onHeaderSelectionChange={this.handleHeaderSelectionChange}
                >
                    <GridColumn field="selected" title="" width={50} headerSelectionValue={allSelected} cell={this.renderCheckboxCell} headerCell={() => <th></th>} />
                    <GridColumn field="transactionDate" title="Transaction Date" cell={DateCell} filter="date" headerCell={this.headerCellRenderers['transactionDate']} />
                    <GridColumn field="startedAt" title="Started At" cell={DateCell} filter="date" headerCell={this.headerCellRenderers['startedAt']} />
                    <GridColumn field="runningTime" title="Hours" cell={TruncCell} headerCell={this.headerCellRenderers['runningTime']} />
                    <GridColumn field="projectName" title="Project" cell={TruncCellLeft} headerCell={this.headerCellRenderers['projectName']} />
                    <GridColumn field="databaseName" title="Database" cell={TruncCellLeft} headerCell={this.headerCellRenderers['databaseName']} />
                    <GridColumn field="templateName" title="Template" cell={TruncCellLeft} headerCell={this.headerCellRenderers['templateName']} />
                    <GridColumn field="fileNames" title="Files" cell={TruncCellLeft} headerCell={this.headerCellRenderers['fileNames']} />
                    <GridColumn field="userName" title="User" cell={TruncCellLeft} headerCell={this.headerCellRenderers['userName']} />
                    <GridColumn field="transactionStatus" title="Status" cell={TruncCellLeft} headerCell={this.headerCellRenderers['transactionStatus']} />
                    <GridColumn field="isTestMode" title="Test Mode" cell={BooleanCell} headerCell={this.headerCellRenderers['isTestMode']} sortable={false} />
                    <GridColumn title="Errors" width={140} cell={this.renderErrorsCell} />
                </Grid>
                {this.state.showErrorsDlg && (
                    <Dialog title={this.state.errorsDlgTitle} onClose={this.closeErrorsDialog} width={900}>
                        <div style={{ maxHeight: '60vh', overflow: 'auto', padding: 0 }}>
                            {this.state.errorsLoading ? (
                                <div><span className="k-icon k-i-loading" /> Loading...</div>
                            ) : (this.state.errorsList && this.state.errorsList.length > 0) ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', boxSizing: 'border-box', margin: '10px 0' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd', boxSizing: 'border-box', width: '10%', position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 2 }}>Type</th>
                                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd', boxSizing: 'border-box', width: '20%', position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 2 }}>Location</th>
                                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd', boxSizing: 'border-box', width: '8%', position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 2 }}>Row</th>
                                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd', boxSizing: 'border-box', width: '10%', position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 2 }}>Number</th>
                                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd', boxSizing: 'border-box', width: '15%', position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 2 }}>Source</th>
                                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd', boxSizing: 'border-box', width: '37%', position: 'sticky', top: 0, background: '#f7f7f7', zIndex: 2 }}>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.errorsList.map((e: any, idx: number) => {
                                            const t = (e.errorType || '').toString().toLowerCase();
                                            const isCritical = t === 'critical' || t === 'failed' || t === 'error';
                                            const isWarning = t === 'warning' || t === 'warn';
                                            const bg = isCritical ? '#ffcccc' : isWarning ? '#ffd9b3' : undefined;
                                            return (
                                                <tr key={idx} style={{ backgroundColor: bg }}>
                                                    <td style={{ padding: '6px', borderBottom: '1px solid #f1f1f1', boxSizing: 'border-box', verticalAlign: 'top', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.errorType}</td>
                                                    <td style={{ padding: '6px', borderBottom: '1px solid #f1f1f1', boxSizing: 'border-box', verticalAlign: 'top', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(e.location || '').replace(/tblImportExcel/g, '')}</td>
                                                    <td style={{ padding: '6px', borderBottom: '1px solid #f1f1f1', boxSizing: 'border-box', verticalAlign: 'top' }}>{e.excelRow}</td>
                                                    <td style={{ padding: '6px', borderBottom: '1px solid #f1f1f1', boxSizing: 'border-box', verticalAlign: 'top' }}>{e.errorNumber}</td>
                                                    <td style={{ padding: '6px', borderBottom: '1px solid #f1f1f1', boxSizing: 'border-box', verticalAlign: 'top', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.source}</td>
                                                    <td style={{ padding: '6px', borderBottom: '1px solid #f1f1f1', boxSizing: 'border-box', verticalAlign: 'top', whiteSpace: 'normal', overflowWrap: 'break-word' }}>{e.description}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{this.state.errorsText || 'No errors found.'}</pre>
                            )}
                        </div>
                        <DialogActionsBar>
                            <button className="k-button k-primary" onClick={this.closeErrorsDialog}>Close</button>
                        </DialogActionsBar>
                    </Dialog>
                )}
            </div>
        );
    }
}

export default connector(ImportTransactions);
